import type {FormEventHandler} from 'react';
import {useEffect, useMemo, useState} from 'react';
import {Button, CircularProgress, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {brand} from "../../../theme/brand";
import LogoWithLanguageSwitcher from '../../../components/common/LogoWithLanguageSwitcher';
import {PageTitleKeys, RoutePaths} from '../../../config/routeConstants';
import {useSnackbar} from '../../../contexts/SnackbarContext';
import {accessTokenKey} from '../../../core/AuthInterceptor';
import {usePageTitle} from '../../../hooks/usePageTitle';
import {parseError} from '../../../errors/http.service';
import {getCookie} from '../../../utils/cookies';
import {AuthContainer} from '../Auth.styled';
import {FormWrapper, LoginCard, StyledTextField} from '../Login/Login.styled';
import {BackButton, Description, EmailText, FieldHint} from './VerifyTwoFactorAuth.styled';
import {authService} from "../../../services/auth.service";
import {termsService} from "../../../services/terms.service";

const VerifyTwoFactorAuth = () => {
    usePageTitle(PageTitleKeys.VerifyTwoFactor);

    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const pendingEmail = useMemo(() => authService.getPendingTwoFactorEmail(), []);

    const [code, setCode] = useState('');
    const [codeTouched, setCodeTouched] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const codeValidationError = useMemo(() => {
        if (!code) {
            return 'twoFactorAuthVerify.codeRequired';
        }

        return /^\d{6}$/.test(code) ? null : 'twoFactorAuthVerify.codeInvalid';
    }, [code]);

    const showCodeError = (codeTouched || submitted) && !!codeValidationError;

    useEffect(() => {
        if (getCookie(accessTokenKey)) {
            navigate(`/${RoutePaths.Chat}`, { replace: true });
            return;
        }

        if (!pendingEmail) {
            navigate(`/${RoutePaths.Login}`, { replace: true });
        }
    }, [navigate, pendingEmail]);

    const handleBackToLogin = () => {
        authService.clearPendingTwoFactorEmail();
        termsService.clearPendingEulaVersion();
        navigate(`/${RoutePaths.Login}`, { replace: true });
    };

    const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
        event.preventDefault();
        setSubmitted(true);

        if (!pendingEmail || codeValidationError) {
            return;
        }

        termsService.clearPendingEulaVersion();
        setIsSubmitting(true);

        try {
            const response = await authService.verifyTwoFactorAuthRequest(pendingEmail, code);

            authService.finalizeAuthentication(response, pendingEmail);
            authService.clearPendingTwoFactorEmail();

            showSnackbar(t('twoFactorAuthVerify.successMessage'), 'success');

            if (response.eulaAcceptanceRequired) {
                termsService.setPendingEulaVersion(response.eulaVersion);
                navigate(`/eula`, { replace: true });
            }

            navigate(`/dashboard`, { replace: true });
        } catch (error) {
            console.error(error);
            showSnackbar(parseError(error) || t('twoFactorAuthVerify.verifyError'), 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!pendingEmail) {
        return null;
    }

    return (
        <AuthContainer role="region" aria-labelledby="verify-2fa-title">
            <LogoWithLanguageSwitcher src={brand.logo} alt={t('auth.logo.alt')} clickable={true} />

            <LoginCard>
                <Typography id="verify-2fa-title" variant="h5" align="center" width="100%" gutterBottom>
                    {t('twoFactorAuthVerify.title')}
                </Typography>
                <Description>
                    {t('twoFactorAuthVerify.description')}
                    {' '}
                    <EmailText>{pendingEmail}</EmailText>
                </Description>

                <FormWrapper onSubmit={handleSubmit} aria-live="polite" aria-atomic={true} aria-busy={isSubmitting}>
                    <div>
                        <StyledTextField
                            label={t('twoFactorAuthVerify.codeLabel')}
                            fullWidth
                            margin="normal"
                            value={code}
                            onChange={(event) =>
                                setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
                            }
                            onBlur={() => setCodeTouched(true)}
                            disabled={isSubmitting}
                            error={showCodeError}
                            helperText={showCodeError ? t(codeValidationError as string) : ''}
                            inputProps={{
                                inputMode: 'numeric',
                                autoComplete: 'one-time-code',
                                'aria-label': t('twoFactorAuthVerify.codeAriaLabel') as string,
                            }}
                        />
                        <FieldHint>{t('twoFactorAuthVerify.codeHint')}</FieldHint>
                    </div>

                    {isSubmitting && (
                        <div
                            role="status"
                            aria-live="polite"
                            style={{
                                position: 'absolute',
                                width: 1,
                                height: 1,
                                overflow: 'hidden',
                                clip: 'rect(1px, 1px, 1px, 1px)',
                            }}
                        >
                            {t('twoFactorAuthVerify.loading')}
                        </div>
                    )}

                    <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting || !code || !!codeValidationError}
                    >
                        {isSubmitting
                            ? <CircularProgress size={24} sx={{ color: 'inherit' }} />
                            : t('twoFactorAuthVerify.submitButton')}
                    </Button>

                    <BackButton
                        type="button"
                        variant="text"
                        onClick={handleBackToLogin}
                        disabled={isSubmitting}
                        aria-label={t('twoFactorAuthVerify.backButtonAriaLabel') as string}
                    >
                        {t('twoFactorAuthVerify.backButton')}
                    </BackButton>
                </FormWrapper>
            </LoginCard>
        </AuthContainer>
    );
};

export default VerifyTwoFactorAuth;
